'use strict';

/**
 * Session-level placement of the left and right EvoWalk devices.
 * TA / Hamstring need an explicit side. RL/RU are right, LL/LU are left.
 */

export const DEVICE_POSITION_OPTIONS = ['TA', 'Hamstring', 'RL', 'RU', 'LL', 'LU'];

const SIDE_FROM_CODE = {
  RL: 'right',
  RU: 'right',
  LL: 'left',
  LU: 'left',
};

export function normalizeDevicePosition(raw) {
  if (raw == null) return null;
  const text = String(raw).trim().toLowerCase();
  if (!text) return null;
  if (text === 'ham' || text === 'hamstring' || text === 'hamstrings') return 'Hamstring';
  const match = DEVICE_POSITION_OPTIONS.find((option) => option.toLowerCase() === text);
  return match || null;
}

function deviceText(device) {
  return ['name', 'position', 'placement', 'location', 'loc', 'side', 'label']
    .map((key) => device[key])
    .filter((value) => value != null && String(value).trim() !== '')
    .join(' ');
}

export function inferDeviceSide(device) {
  if (!device || typeof device !== 'object') return null;
  const side = String(device.side == null ? '' : device.side).trim().toLowerCase();
  if (side === 'left' || side === 'l') return 'left';
  if (side === 'right' || side === 'r') return 'right';
  const blob = deviceText(device).toLowerCase();
  if (/\bright\b/.test(blob)) return 'right';
  if (/\bleft\b/.test(blob)) return 'left';
  return null;
}

export function inferDevicePosition(device) {
  if (!device || typeof device !== 'object') return null;
  const explicit = normalizeDevicePosition(
    device.position || device.placement || device.location || device.loc
  );
  if (explicit) return explicit;
  const blob = deviceText(device).toLowerCase();
  if (/\bham(string|strings)?\b/.test(blob)) return 'Hamstring';
  if (/(^|[^a-z])ta([^a-z]|$)/.test(blob)) return 'TA';
  if (/(^|[^a-z])rl([^a-z]|$)/.test(blob)) return 'RL';
  if (/(^|[^a-z])ru([^a-z]|$)/.test(blob)) return 'RU';
  if (/(^|[^a-z])ll([^a-z]|$)/.test(blob)) return 'LL';
  if (/(^|[^a-z])lu([^a-z]|$)/.test(blob)) return 'LU';
  return null;
}

function isConnected(device) {
  if (!device || typeof device !== 'object') return false;
  return device.connected === true || device.connected === 'yes' || device.connected === 1;
}

function unique(values) {
  return values.filter((value, index) => values.indexOf(value) === index);
}

/**
 * Positions implied by connected devices. A side is filled only when exactly
 * one distinct position is known for it.
 */
export function suggestDevicePositions(devices) {
  const list = Array.isArray(devices) ? devices : [];
  const buckets = { left: [], right: [] };
  list.filter(isConnected).forEach((device) => {
    const position = inferDevicePosition(device);
    if (!position) return;
    const side = inferDeviceSide(device) || SIDE_FROM_CODE[position] || null;
    if (!side) return;
    buckets[side].push(position);
  });
  const pick = (side) => {
    const values = unique(buckets[side]);
    return values.length === 1 ? values[0] : '';
  };
  return { leftpos: pick('left'), rightpos: pick('right') };
}

/** Fills empty left/right positions. Values the clinician already set stay. */
export function applyDevicePositionAutofill(sessionTags, devices) {
  const current = sessionTags && typeof sessionTags === 'object' && !Array.isArray(sessionTags)
    ? sessionTags
    : {};
  const suggested = suggestDevicePositions(devices);
  const next = { ...current };
  ['leftpos', 'rightpos'].forEach((key) => {
    const existing = current[key];
    if (existing != null && String(existing).trim() !== '') return;
    if (suggested[key]) next[key] = suggested[key];
  });
  return next;
}

function basename(value) {
  const text = String(value).trim();
  const cut = Math.max(text.lastIndexOf('/'), text.lastIndexOf('\\'));
  return cut === -1 ? text : text.slice(cut + 1);
}

function downloadedFileDevice(file) {
  if (typeof file === 'string') {
    return { name: file, side: null, position: null };
  }
  if (!file || typeof file !== 'object') return null;
  const named = file.device_name || file.key || file.name || file.filename || file.path || '';
  return {
    name: named,
    side: file.device_side != null ? file.device_side : file.side,
    position: file.position || file.placement || file.location || file.loc || null,
  };
}

/**
 * Files the recorder just pulled off the EvoWalks, from a stop-record body
 * (`device_files` / `evowalk_files`) or from `GET /a/check-evowalk-raw`.
 * Returns null when this payload does not carry a file list.
 */
export function deviceFilesFromDownloadPayload(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  if (Array.isArray(payload.device_files)) return payload.device_files;
  if (Array.isArray(payload.evowalk_files)) return payload.evowalk_files;
  if (Array.isArray(payload.files) && (payload.hasData != null || payload.status != null)) {
    return payload.files;
  }
  return null;
}

/**
 * Trial tag values implied by the device files downloaded for one trial.
 * A side with two different placements is left blank. No recognizable file
 * leaves every tag blank.
 */
export function deviceTagsFromDownloadedFiles(files) {
  const list = Array.isArray(files) ? files : [];
  const buckets = { left: [], right: [] };
  list.forEach((file) => {
    const parsed = downloadedFileDevice(file);
    if (!parsed) return;
    const probe = {
      name: basename(parsed.name),
      side: parsed.side,
      position: parsed.position,
    };
    if (!probe.name && !probe.side && !probe.position) return;
    const position = inferDevicePosition(probe);
    const side = inferDeviceSide(probe) || (position && SIDE_FROM_CODE[position]) || null;
    if (!side) return;
    buckets[side].push(position || '');
  });
  const pick = (side) => {
    const positions = unique(buckets[side].filter(Boolean));
    return positions.length === 1 ? positions[0] : '';
  };
  const sawLeft = buckets.left.length > 0;
  const sawRight = buckets.right.length > 0;
  let device = '';
  if (sawLeft && sawRight) device = 'Both';
  else if (sawLeft) device = 'Left';
  else if (sawRight) device = 'Right';
  return { device, leftpos: pick('left'), rightpos: pick('right') };
}
